import { ProjectSchemeInterface, ProjectSchemePhase } from "ts-interfaces";
import { CURRENCY_OPTIONS, currencyLabel } from "../../../libs/currency";
import { budgetDisplay } from "./projectBudget";

// The full shared currency list (#3593) — one vocabulary with the FF&E
// schedule display and the profile-derived defaults (libs/currency.ts).
export const PROJECT_CURRENCIES = CURRENCY_OPTIONS;
export const PROJECT_MEASUREMENT_UNITS = ["Imperial", "Metric"];

// `format` labels an option/value ("USD" -> "USD $"; the stored value stays
// raw). `display` derives the overview cell from the whole project, for fields
// whose truth lives in structured siblings (#3787). `kind` opts a field into a
// purpose-built control over text/select.
export interface ProjectDetailField {
  label: string;
  field: string;
  type?: "text" | "date";
  options?: string[];
  format?: (value: string) => string;
  display?: (project: ProjectSchemeInterface) => string;
  kind?: "city" | "money";
}

// Shared contract of the structured field widgets (#3787): the whole draft in,
// a multi-key patch out, so one gesture writes its keys atomically.
export interface StructuredFieldProps {
  label: string;
  /** Stable id for deep-link focus and label association (getByLabelText). */
  id: string;
  project: Partial<ProjectSchemeInterface>;
  onChange: (patch: Partial<ProjectSchemeInterface>) => void;
  /** Render `label` as MUI's floating label (legacy creator dialog mode). */
  floatingLabel?: boolean;
}

// Always-visible metadata row on the project overview (#3606 mockup).
export const PRIMARY_FIELDS: ProjectDetailField[] = [
  // Project Type carries no options here: EditProjectModal supplies them at
  // render via projectTypeOptions(current) so a legacy value stays selectable.
  { label: "Project Type", field: "type" },
  // Structured inputs (#3787): city autocomplete and validated amount/range.
  { label: "Location", field: "location", kind: "city" },
  {
    label: "Project Budget",
    field: "budget",
    kind: "money",
    display: budgetDisplay,
  },
];

// Revealed by the overview's "Show More" toggle.
export const MORE_FIELDS: ProjectDetailField[] = [
  {
    label: "Phase",
    field: "phase",
    options: Object.values(ProjectSchemePhase),
  },
  { label: "Size", field: "size" },
  { label: "Est. Completion", field: "estCompletionDate", type: "date" },
  {
    label: "Currency",
    field: "currency",
    options: PROJECT_CURRENCIES,
    format: currencyLabel,
  },
  {
    label: "Measurements",
    field: "measurementUnit",
    options: PROJECT_MEASUREMENT_UNITS,
  },
  { label: "Target Date", field: "startDate", type: "date" },
];

// Edit Project Details modal field order (name + description render
// separately above this grid).
export const EDIT_FIELDS: ProjectDetailField[] = [
  ...PRIMARY_FIELDS,
  ...MORE_FIELDS,
];
